<script setup>
import { computed, ref } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ConversationCard from 'dashboard/components-next/Conversation/ConversationCard/ConversationCard.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();

const page = ref(1);

const conversations = useMapGetter(
  'contactConversations/getAllConversationsByContactId'
);
const contactsById = useMapGetter('contacts/getContactById');
const stateInbox = useMapGetter('inboxes/getInboxById');
const accountLabels = useMapGetter('labels/getLabels');

const accountLabelsValue = computed(() => accountLabels.value);

const uiFlags = useMapGetter('contactConversations/getUIFlags');
const isFetching = computed(() => uiFlags.value.isFetching);
const isAllFetched = computed(() => uiFlags.value.isAllFetched);

const contactConversations = computed(() =>
  conversations.value(route.params.contactId)
);

const showLoadMore = computed(
  () =>
    !isAllFetched.value &&
    !isFetching.value &&
    contactConversations.value.length > 0
);

const loadMoreConversations = () => {
  if (isFetching.value || isAllFetched.value) return;

  page.value += 1;
  store.dispatch('contactConversations/get', {
    contactId: route.params.contactId,
    page: page.value,
  });
};
</script>

<template>
  <div
    v-if="isFetching && contactConversations.length === 0"
    class="flex items-center justify-center py-10 text-n-slate-11"
  >
    <Spinner />
  </div>
  <div
    v-else-if="contactConversations.length > 0"
    class="px-6 py-4 divide-y divide-n-strong [&>*:hover]:!border-y-transparent [&>*:hover+*]:!border-t-transparent"
  >
    <ConversationCard
      v-for="conversation in contactConversations"
      :key="conversation.id"
      :conversation="conversation"
      :contact="contactsById(conversation.meta.sender.id)"
      :state-inbox="stateInbox(conversation.inboxId)"
      :account-labels="accountLabelsValue"
      class="rounded-none hover:rounded-xl hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3"
    />
    <div v-if="showLoadMore" class="flex items-center justify-center py-3">
      <button
        class="text-xs font-medium text-n-blue-text hover:underline"
        @click="loadMoreConversations"
      >
        {{ t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.LOAD_MORE') }}
      </button>
    </div>
    <div
      v-if="isFetching && page > 1"
      class="flex items-center justify-center py-3"
    >
      <Spinner />
    </div>
    <p
      v-if="isAllFetched && contactConversations.length > 0"
      class="py-3 text-center text-xs text-n-slate-11"
    >
      {{ t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.ALL_LOADED') }}
    </p>
  </div>
  <p v-else class="px-6 py-10 text-sm leading-6 text-center text-n-slate-11">
    {{ t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.EMPTY_STATE') }}
  </p>
</template>
